import { useCallback, useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import type { TFunction } from "i18next"

import { useAppDispatch } from "@/core/app/hooks"
import type { AppDispatch } from "@/core/app/store"

import {
  cleanupScannerFiles,
  downloadScannerFile,
  loadScannerFiles,
} from "./integrationsThunkApi"
import { setAttachmentsLoading } from "./integrationsSlice"
import { setAlert } from "@/core/app/slices/alert/alertSlice"
import type { ScannerFileEntry, ScannerFileGroupKey } from "./integrations.type"

const POLL_INTERVAL_MS = 2000
const MAX_POLL_ATTEMPTS = 500 //Infinity

/**
 * Master toggle for all scanner import feedback toasts.
 * Controls success toasts ("Imported … from Scanner"),
 * error toasts ("Failed to load/download from Scanner"),
 * and info toasts ("No data found / timed out").
 * Set to false to suppress all of them.
 */
const SCANNER_IMPORT_TOAST_ENABLED = true

// Shows which scan files were successfully imported; "info" severity when none found.
function dispatchScanImportToast(
  dispatch: AppDispatch,
  t: TFunction,
  hasUpper: boolean,
  hasLower: boolean,
) {
  let message: string
  if (hasUpper && hasLower) {
    message = t("scanner.scans.upperAndLowerImported")
  } else if (hasUpper) {
    message = t("scanner.scans.onlyUpperImported")
  } else if (hasLower) {
    message = t("scanner.scans.onlyLowerImported")
  } else {
    message = t("scanner.scans.noScansImported")
  }
  dispatch(setAlert({ message, severity: hasUpper || hasLower ? "success" : "info" }))
}

// Shows how many photos were imported; "info" severity when none found.
function dispatchPhotosImportToast(dispatch: AppDispatch, t: TFunction, count: number) {
  if (count > 0) {
    dispatch(setAlert({
      message: t("scanner.photos.allPhotosImported", { count, fileText: count === 1 ? "file" : "files" }),
      severity: "success",
    }))
  } else {
    dispatch(setAlert({
      message: t("scanner.photos.noPhotosImported"),
      severity: "info",
    }))
  }
}

export interface ScannerFilesLoaderOptions {
  externalCaseId: string | number
  scannerName: string
  requiredKeys: ScannerFileGroupKey[]
  onUpperReady?: (file: File | null) => void
  onLowerReady?: (file: File | null) => void
  /** Called once when ALL photos are downloaded. Awaited before cleanup fires. */
  onPhotosReady?: (files: File[]) => void | Promise<void>
  onError?: () => void
}

interface PollState {
  downloadedIds: Set<number>
  photoDownloadPromises: Promise<File | null>[]
  upperFilePromise: Promise<File | null> | null
  lowerFilePromise: Promise<File | null> | null
  upperQueued: boolean
  lowerQueued: boolean
}

// Transforms a completed file entry into a File download promise.
// dispatch returns an action object, so .then() unwraps it to File | null.
// Download failures resolve to null — the scan/photo import result toast reflects the outcome.
function queueFileDownload(
  dispatch: AppDispatch,
  downloadedIds: Set<number>,
  entry: ScannerFileEntry,
  scannerName: string,
): Promise<File | null> {
  if (downloadedIds.has(entry.attachment_id) || !entry.download_url) {
    return Promise.resolve(null)
  }
  downloadedIds.add(entry.attachment_id)
  return dispatch(
    downloadScannerFile({ url: entry.download_url, fileName: entry.name, scannerName }),
  ).then((action) => {
    if (downloadScannerFile.fulfilled.match(action)) return action.payload
    console.error("Failed to download scanner file:", entry.name)
    return null
  })
}

// Exit poll loop once all required file groups are "completed" on the server.
function checkAllComplete(
  requiredKeys: ScannerFileGroupKey[],
  upper: ScannerFileEntry | null | undefined,
  lower: ScannerFileEntry | null | undefined,
  photos: ScannerFileEntry[],
): boolean {
  return requiredKeys.every((key) => {
    if (key === "upper") return !upper || upper.status === "completed"
    if (key === "lower") return !lower || lower.status === "completed"
    if (key === "photos") return photos.every((p) => p.status === "completed")
    return true
  })
}

// Queue downloads as soon as the server marks files "completed" (non-blocking).
// upperQueued / lowerQueued flags guard ensure we only queue each once.
function processCompletedFiles(
  requiredKeys: ScannerFileGroupKey[],
  upper: ScannerFileEntry | null | undefined,
  lower: ScannerFileEntry | null | undefined,
  photos: ScannerFileEntry[],
  state: PollState,
  dispatch: AppDispatch,
  scannerName: string,
  onUpperReady?: (file: File | null) => void,
  onLowerReady?: (file: File | null) => void,
): void {
  if (requiredKeys.includes("upper") && upper?.status === "completed" && !state.upperQueued) {
    state.upperQueued = true
    state.upperFilePromise = queueFileDownload(dispatch, state.downloadedIds, upper, scannerName)
    state.upperFilePromise.then((file) => file && onUpperReady?.(file))
  }
  if (requiredKeys.includes("lower") && lower?.status === "completed" && !state.lowerQueued) {
    state.lowerQueued = true
    state.lowerFilePromise = queueFileDownload(dispatch, state.downloadedIds, lower, scannerName)
    state.lowerFilePromise.then((file) => file && onLowerReady?.(file))
  }
  if (requiredKeys.includes("photos")) {
    photos
      .filter((p) => p.status === "completed")
      .forEach((p) => state.photoDownloadPromises.push(queueFileDownload(dispatch, state.downloadedIds, p, scannerName)))
  }
}

// Signal removal for required keys absent on the server (server confirmed no file).
function signalAbsentFiles(
  requiredKeys: ScannerFileGroupKey[],
  state: PollState,
  onUpperReady?: (file: File | null) => void,
  onLowerReady?: (file: File | null) => void,
): void {
  if (requiredKeys.includes("upper") && !state.upperQueued) onUpperReady?.(null)
  if (requiredKeys.includes("lower") && !state.lowerQueued) onLowerReady?.(null)
}

// Await in-flight scan downloads, then show import result toast.
async function showScanImportToast(
  dispatch: AppDispatch,
  t: TFunction,
  requiredKeys: ScannerFileGroupKey[],
  state: PollState,
): Promise<void> {
  const hasScanKeys = requiredKeys.includes("upper") || requiredKeys.includes("lower")
  if (!SCANNER_IMPORT_TOAST_ENABLED || !hasScanKeys) return
  const [upperFile, lowerFile] = await Promise.all([
    state.upperFilePromise ?? Promise.resolve(null),
    state.lowerFilePromise ?? Promise.resolve(null),
  ])
  dispatchScanImportToast(dispatch, t, upperFile instanceof File, lowerFile instanceof File)
}

// Await all photo downloads, invoke onPhotosReady, then show toast and cleanup.
// Cleanup runs after onPhotosReady resolves to avoid deleting files mid-upload.
async function handlePhotosCompletion(
  dispatch: AppDispatch,
  t: TFunction,
  externalCaseId: string | number,
  scannerName: string,
  photoDownloadPromises: Promise<File | null>[],
  onPhotosReady?: (files: File[]) => void | Promise<void>,
): Promise<void> {
  let photosCount = 0
  if (onPhotosReady && photoDownloadPromises.length) {
    const settled = await Promise.all(photoDownloadPromises)
    const validFiles = settled.filter((f): f is File => f instanceof File)
    photosCount = validFiles.length
    if (validFiles.length) await onPhotosReady(validFiles)
  }
  if (SCANNER_IMPORT_TOAST_ENABLED) dispatchPhotosImportToast(dispatch, t, photosCount)
  dispatch(cleanupScannerFiles({ externalCaseId, scannerName }))
}

// Dispatches the appropriate failure toast based on which file group this hook was loaded for.
// This hook is used either for scans or for photos, never both at once,
// so only one failure message will ever fire here.
function dispatchImportFailureToast(
  dispatch: AppDispatch,
  t: TFunction,
  requiredKeys: ScannerFileGroupKey[],
): void {
  if (requiredKeys.includes("upper") || requiredKeys.includes("lower")) {
    dispatch(setAlert({ message: t("scanner.scans.failedToImport"), severity: "error" }))
  } else if (requiredKeys.includes("photos")) {
    dispatch(setAlert({ message: t("scanner.photos.failedToImport"), severity: "error" }))
  } else {
    // Unexpected requiredKeys combination — no user-facing toast needed.
    console.error("Failed to import scanner files for keys:", requiredKeys)
  }
}

function waitMs(ms: number, timerRef: { current: ReturnType<typeof setTimeout> | null }): Promise<void> {
  return new Promise<void>((resolve) => {
    timerRef.current = setTimeout(resolve, ms)
  })
}

export function useScannerFilesLoader() {
  const dispatch = useAppDispatch()
  const { t } = useTranslation("common")
  // Incremented on each startLoading call or unmount to invalidate stale poll iterations.
  const pollSessionRef = useRef(0)
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const startLoading = useCallback(
    async ({
      externalCaseId,
      scannerName,
      requiredKeys,
      onUpperReady,
      onLowerReady,
      onPhotosReady,
      onError,
    }: ScannerFilesLoaderOptions) => {
      const session = ++pollSessionRef.current
      const state: PollState = {
        downloadedIds: new Set<number>(),
        photoDownloadPromises: [],
        upperFilePromise: null,
        lowerFilePromise: null,
        upperQueued: false,
        lowerQueued: false,
      }

      dispatch(setAttachmentsLoading("pending"))

      try {
        let allServerComplete = false

        for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
          if (session !== pollSessionRef.current) break

          const result = await dispatch(
            loadScannerFiles({ externalCaseId, scannerName, isPolling: true }),
          ).unwrap()

          if (session !== pollSessionRef.current) break

          const { upper, lower, photos } = result.files
          processCompletedFiles(requiredKeys, upper, lower, photos, state, dispatch, scannerName, onUpperReady, onLowerReady)
          allServerComplete = checkAllComplete(requiredKeys, upper, lower, photos)

          if (allServerComplete) break

          await waitMs(POLL_INTERVAL_MS, pollTimerRef)
          pollTimerRef.current = null
        }

        if (!allServerComplete && isFinite(MAX_POLL_ATTEMPTS) && session === pollSessionRef.current) {
          dispatch(setAlert({ message: "Fetching data from scanner timed out.", severity: "info", timeout: 5000 }))
        }

        if (allServerComplete) {
          signalAbsentFiles(requiredKeys, state, onUpperReady, onLowerReady)
          await showScanImportToast(dispatch, t, requiredKeys, state)
        }

        if (requiredKeys.includes("photos")) {
          await handlePhotosCompletion(dispatch, t, externalCaseId, scannerName, state.photoDownloadPromises, onPhotosReady)
        }

        dispatch(setAttachmentsLoading("succeeded"))
      } catch(err) {
        console.error("Scanner file load failed:", err)
        dispatch(setAttachmentsLoading("failed"))
        // loadScannerFiles rejection propagates here via .unwrap(); skip toast if session was
        // already invalidated (e.g. dialog closed mid-flight).
        if (SCANNER_IMPORT_TOAST_ENABLED && session === pollSessionRef.current) {
          dispatchImportFailureToast(dispatch, t, requiredKeys)
        }
        onError?.()
      }
    },
    [dispatch, t],
  )

  // Invalidate any in-flight poll session on unmount to prevent stale state updates.
  useEffect(() => {
    return () => {
      pollSessionRef.current += 1
      if (pollTimerRef.current !== null) {
        clearTimeout(pollTimerRef.current)
        pollTimerRef.current = null
      }
    }
  }, [])

  return { startLoading }
}
